import { useEffect, useRef, useState } from 'react';
import { Banner, Toast } from '../primitives';
import {
  getStuckOfflineThresholdMs,
  useSyncDiagnosticsVM
} from '../../lib/vm/useSyncDiagnosticsVM';

export interface SyncDiagnosticsAlertsProps {
  /** Currently active org id from the shell. When null (no org selected /
   *  loading / empty memberships), the alerts surface stays silent — banners
   *  only fire in the shell chrome bound to the currently-active org. */
  activeOrgId: string | null;
}

/**
 * Shell-level alerts derived from `useSyncDiagnosticsVM`'s connectivity status:
 *   • A stuck-offline `<Banner>` after `status === 'offline'` for ≥ 5 minutes
 *     (threshold configurable via `window.__penroseStuckOfflineThresholdMs`).
 *   • A one-shot back-online `<Toast>` when status flips `offline → online`.
 *
 * Renders nothing when `activeOrgId` is null — keeps the unauth / no-org
 * states clean. Mounting is unconditional inside `<SignedInShell>` so the
 * status transitions are observed regardless of which surface is open.
 */
export function SyncDiagnosticsAlerts({ activeOrgId }: SyncDiagnosticsAlertsProps) {
  const { state } = useSyncDiagnosticsVM();
  const [offlineSince, setOfflineSince] = useState<number | null>(null);
  const [backOnlineAt, setBackOnlineAt] = useState<number | null>(null);
  const [, forceTick] = useState(0);
  const prevStatusRef = useRef(state.status);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = state.status;
    if (state.status === 'offline' && prev !== 'offline') {
      setOfflineSince(Date.now());
      setBackOnlineAt(null);
    } else if (state.status !== 'offline' && prev === 'offline') {
      setOfflineSince(null);
      setBackOnlineAt(Date.now());
    }
  }, [state.status]);

  // Tick once a second while offline so the threshold check re-evaluates.
  useEffect(() => {
    if (offlineSince === null) return;
    const iv = window.setInterval(() => forceTick((n) => n + 1), 1000);
    return () => window.clearInterval(iv);
  }, [offlineSince]);

  if (activeOrgId === null) return null;

  const threshold = getStuckOfflineThresholdMs();
  const stuck =
    offlineSince !== null && Date.now() - offlineSince >= threshold;

  return (
    <>
      {stuck ? (
        <Banner
          kind="warn"
          title="No network"
          className="pn-stuck-offline"
          data-testid="sync-diagnostics-stuck-offline-banner"
        >
          We&rsquo;ve been offline for several minutes. Recent changes
          haven&rsquo;t synced yet.
        </Banner>
      ) : null}
      {backOnlineAt !== null ? (
        <Toast
          title="Back online"
          durationMs={4500}
          onDismiss={() => setBackOnlineAt(null)}
          className="pn-toast-back-online"
        >
          Your changes are syncing.
        </Toast>
      ) : null}
    </>
  );
}
